/**
 * StagedNoticeRecipientsList — who the "staged order changed" email goes to.
 *
 * Shown inside the Edit-in-Sage override prompt (review ifevp1, Scott
 * 2026-10-16: "it should say which emails the notification will go to and
 * they should be able to add/delete/edit those emails"). The list itself is
 * shared and persisted (/portal/staged-notice-recipients): add, edit and
 * remove change it for everyone and are kept as history server-side. The
 * tick on each row is per push only: an unticked address is skipped this
 * time without being taken off the list.
 */
import React, { useEffect, useState } from 'react';
import { portalOrderService, type StagedNoticeRecipient } from '../../services/portalOrders';

interface Props {
  /** Which addresses the push will email (controlled by the modal). */
  selected: string[];
  onSelectedChange: (emails: string[]) => void;
  /** The order the prompt is about, for the UI audit rows. */
  orderId: string;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const s = {
  row: { display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12.5 } as React.CSSProperties,
  input: {
    padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12.5, minWidth: 0,
  } as React.CSSProperties,
  btn: {
    padding: '2px 8px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff',
    cursor: 'pointer', fontSize: 12, color: '#374151',
  } as React.CSSProperties,
  muted: { color: '#6b7280', fontSize: 12 } as React.CSSProperties,
  err: { color: '#b91c1c', fontSize: 12 } as React.CSSProperties,
};

const StagedNoticeRecipientsList: React.FC<Props> = ({ selected, onSelectedChange, orderId }) => {
  const [rows, setRows] = useState<StagedNoticeRecipient[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editName, setEditName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addName, setAddName] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = async () => {
    try {
      const list = await portalOrderService.listStagedNoticeRecipients();
      setRows(list);
      setLoadError(null);
      return list;
    } catch {
      setRows([]);
      setLoadError('Could not load the recipient list.');
      return [];
    }
  };

  useEffect(() => {
    let cancelled = false;
    portalOrderService
      .listStagedNoticeRecipients()
      .then((list) => { if (!cancelled) { setRows(list); setLoadError(null); } })
      .catch(() => { if (!cancelled) { setRows([]); setLoadError('Could not load the recipient list.'); } });
    return () => { cancelled = true; };
  }, []);

  const apiError = (e: unknown, fallback: string): string => {
    const err = e as { response?: { data?: { detail?: string } }; message?: string };
    return err?.response?.data?.detail || err?.message || fallback;
  };

  const toggle = (email: string, on: boolean) => {
    const set = new Set(selected);
    if (on) set.add(email); else set.delete(email);
    onSelectedChange(Array.from(set));
  };

  const startEdit = (r: StagedNoticeRecipient) => {
    setEditingId(r.id);
    setEditEmail(r.email);
    setEditName(r.name ?? '');
    setActionError(null);
  };

  const saveEdit = async (r: StagedNoticeRecipient) => {
    const email = editEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { setActionError(`'${editEmail}' is not an email address.`); return; }
    setBusy(true);
    setActionError(null);
    try {
      const updated = await portalOrderService.updateStagedNoticeRecipient(r.id, email, editName.trim() || null);
      // Keep the per-push tick on the corrected address.
      const wasSelected = selected.includes(r.email);
      const next = selected.filter((e) => e !== r.email);
      onSelectedChange(wasSelected ? [...next, updated.email] : next);
      setEditingId(null);
      await load();
    } catch (e) {
      setActionError(apiError(e, 'Could not save the change.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: StagedNoticeRecipient) => {
    setBusy(true);
    setActionError(null);
    try {
      await portalOrderService.removeStagedNoticeRecipient(r.id);
      onSelectedChange(selected.filter((e) => e !== r.email));
      await load();
    } catch (e) {
      setActionError(apiError(e, 'Could not remove the address.'));
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    const email = addEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { setActionError(`'${addEmail}' is not an email address.`); return; }
    setBusy(true);
    setActionError(null);
    try {
      const created = await portalOrderService.addStagedNoticeRecipient(email, addName.trim() || null);
      onSelectedChange([...selected.filter((e) => e !== created.email), created.email]);
      setAddEmail('');
      setAddName('');
      await load();
    } catch (e) {
      setActionError(apiError(e, 'Could not add the address.'));
    } finally {
      setBusy(false);
    }
  };

  const ticked = (rows ?? []).filter((r) => selected.includes(r.email)).map((r) => r.email);

  return (
    <div data-testid="staged-notice-recipients">
      <div style={{ fontWeight: 600, marginBottom: 2 }}>
        Send email to{' '}
        <span style={{ fontWeight: 400 }}>
          {ticked.length > 0 ? ticked.join(', ') : 'nobody yet'}
          {' '}(and you, cc)
        </span>
      </div>
      {rows === null && <div style={s.muted}>Loading the recipient list…</div>}
      {loadError && <div style={s.err}>{loadError}</div>}
      {rows && rows.length === 0 && !loadError && (
        <div style={s.muted}>The list is empty. Add an address below.</div>
      )}
      {(rows ?? []).map((r) => (
        <div key={r.id} style={s.row} data-testid="staged-notice-recipient-row">
          {editingId === r.id ? (
            <>
              <input
                style={{ ...s.input, flex: 2 }}
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                aria-label={`Edit email ${r.email}`}
              />
              <input
                style={{ ...s.input, flex: 1 }}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Name (optional)"
                aria-label={`Edit name ${r.email}`}
              />
              <button
                type="button"
                style={{ ...s.btn, background: '#2563eb', color: '#fff', borderColor: '#2563eb' }}
                onClick={() => void saveEdit(r)}
                disabled={busy}
                data-action="staged_notice_recipient_save"
                data-target-order-id={orderId}
                data-target-recipient-id={r.id}
                data-outcome="request"
              >
                Save
              </button>
              <button
                type="button"
                style={s.btn}
                onClick={() => setEditingId(null)}
                data-action="staged_notice_recipient_edit_cancel"
                data-target-order-id={orderId}
                data-target-recipient-id={r.id}
                data-outcome="none"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                <input
                  type="checkbox"
                  checked={selected.includes(r.email)}
                  onChange={(e) => toggle(r.email, e.target.checked)}
                  aria-label={`Email ${r.email} this time`}
                  data-action="staged_notice_recipient_toggle"
                  data-target-order-id={orderId}
                  data-target-recipient-id={r.id}
                  data-outcome="none"
                />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.email}
                  {r.name ? <span style={s.muted}> · {r.name}</span> : null}
                </span>
              </label>
              <button
                type="button"
                style={s.btn}
                onClick={() => startEdit(r)}
                disabled={busy}
                aria-label={`Edit ${r.email}`}
                data-action="staged_notice_recipient_edit_open"
                data-target-order-id={orderId}
                data-target-recipient-id={r.id}
                data-outcome="open"
              >
                ✎
              </button>
              <button
                type="button"
                style={{ ...s.btn, color: '#b91c1c' }}
                onClick={() => void remove(r)}
                disabled={busy}
                aria-label={`Remove ${r.email}`}
                title="Take this address off the list (kept in the history)"
                data-action="staged_notice_recipient_remove"
                data-target-order-id={orderId}
                data-target-recipient-id={r.id}
                data-outcome="request"
              >
                ✕
              </button>
            </>
          )}
        </div>
      ))}
      <div style={{ ...s.row, marginTop: 4 }}>
        <input
          style={{ ...s.input, flex: 2 }}
          value={addEmail}
          onChange={(e) => setAddEmail(e.target.value)}
          placeholder="name@chemmasters.net"
          aria-label="New recipient email"
        />
        <input
          style={{ ...s.input, flex: 1 }}
          value={addName}
          onChange={(e) => setAddName(e.target.value)}
          placeholder="Name (optional)"
          aria-label="New recipient name"
        />
        <button
          type="button"
          style={s.btn}
          onClick={() => void add()}
          disabled={busy || !addEmail.trim()}
          data-action="staged_notice_recipient_add"
          data-target-order-id={orderId}
          data-outcome="request"
        >
          + Add
        </button>
      </div>
      {actionError && <div style={s.err} data-testid="staged-notice-recipients-error">{actionError}</div>}
      <div style={s.muted}>Changes to the list are saved for everyone; untick an address to skip it this time only.</div>
    </div>
  );
};

export default StagedNoticeRecipientsList;
